import {
  Box,
  Checkbox,
  Chip,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { GetApp } from '@material-ui/icons';
import { Button, FileUploadArea, PaginationBar, StandardDialog } from '@roc/ui';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useDebtTrackingStore } from '../../hooks/useDebtTrackingStore';
import {
  BULK_IMPORT_COLUMNS,
  BULK_IMPORT_FILE_TYPES,
  BULK_IMPORT_PREVIEW_PAGE_SIZE,
  BulkImportAction,
  BulkImportRow,
} from '../../utils/debtTrackingConstants';
import { downloadBulkImportTemplate, formatDisplayDate, formatMoney, pluralize } from '../../utils/debtTrackingUtils';

const useStyles = makeStyles((theme: Theme) => ({
  pagination: {
    marginTop: theme.spacing(1),
  },
  summaryCell: {
    border: '1px solid #cdcdcd',
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
  },
  summaryError: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
  summaryLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: 'inherit',
    opacity: 0.8,
  },
  summaryValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
  },
  previewTable: {
    maxHeight: '45vh',
    border: '1px solid #cdcdcd',
  },
  errorRow: {
    backgroundColor: '#FDF2F1',
  },
  issues: {
    color: theme.palette.error.main,
  },
  columnChip: {
    margin: theme.spacing(0.5, 0.5, 0, 0),
  },
  newBadge: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
  },
  updateBadge: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  errorBadge: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
  },
}));

type PreviewFilter = 'all' | BulkImportAction;

const PREVIEW_FILTERS: { value: PreviewFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: BulkImportAction.NEW, label: 'New' },
  { value: BulkImportAction.UPDATE, label: 'Updates' },
  { value: BulkImportAction.ERROR, label: 'Errors' },
];

export const BulkImportDialog = observer(({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const classes = useStyles();
  const { debtTrackingStore } = useDebtTrackingStore();
  const { bulkImportPreview, bulkImportResult, includedBulkImportRows, warehouses } = debtTrackingStore;
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>('all');
  const [previewPage, setPreviewPage] = useState(1);
  const [uploadError, setUploadError] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (open) {
      debtTrackingStore.resetBulkImport();
      setPreviewFilter('all');
      setPreviewPage(1);
      setUploadError('');
    }
  }, [open]);

  const rows = bulkImportPreview?.rows ?? [];
  const notFoundLoanIds = bulkImportPreview?.notFoundLoanIds ?? [];
  const countOf = (filter: PreviewFilter) => (filter === 'all' ? rows.length : rows.filter(row => row.action === filter).length);
  const filteredRows = previewFilter === 'all' ? rows : rows.filter(row => row.action === previewFilter);
  // The preview isn't persisted (it comes back from the upload in one response), so it is paginated client-side.
  const previewTotalPages = Math.max(1, Math.ceil(filteredRows.length / BULK_IMPORT_PREVIEW_PAGE_SIZE));
  const previewPageNumber = Math.min(previewPage, previewTotalPages);
  const previewStart = (previewPageNumber - 1) * BULK_IMPORT_PREVIEW_PAGE_SIZE;
  const visibleRows = filteredRows.slice(previewStart, previewStart + BULK_IMPORT_PREVIEW_PAGE_SIZE);
  const affectedWarehouses = new Set(includedBulkImportRows.map(row => row.warehouseName)).size;
  const errorCount = countOf(BulkImportAction.ERROR);

  const uploadFile = async (file: File) => {
    if (!file) return;
    setIsWorking(true);
    setUploadError('');
    const errorMessage = await flowResult(debtTrackingStore.previewBulkImport(file));
    setIsWorking(false);
    if (errorMessage) setUploadError(errorMessage);
  };

  const applyImport = async () => {
    setIsWorking(true);
    await flowResult(debtTrackingStore.applyBulkImport());
    setIsWorking(false);
  };

  const summaryCell = (label: string, value: string | number, isError = false) => (
    <Grid item xs={3}>
      <Box className={`${classes.summaryCell} ${isError ? classes.summaryError : ''}`}>
        <Typography className={classes.summaryLabel}>{label}</Typography>
        <Typography className={classes.summaryValue}>{value}</Typography>
      </Box>
    </Grid>
  );

  const actionBadge = (row: BulkImportRow) => {
    const badges = {
      [BulkImportAction.NEW]: { label: 'Create loan', className: classes.newBadge },
      [BulkImportAction.UPDATE]: { label: 'Update amount', className: classes.updateBadge },
      [BulkImportAction.ERROR]: { label: 'Skip · error', className: classes.errorBadge },
    };
    const badge = badges[row.action];
    return (
      <Box display="flex" style={{ gap: 4 }}>
        <Chip size="small" label={badge.label} className={badge.className} />
        {!row.include && row.action !== BulkImportAction.ERROR && <Chip size="small" label="Excluded" />}
      </Box>
    );
  };

  const uploadStep = (
    <Box m={2} flexGrow={1} minWidth={0}>
      <FileUploadArea
        items={[]}
        fileTypes={BULK_IMPORT_FILE_TYPES}
        setItems={update => {
          const items = typeof update === 'function' ? update([]) : update;
          uploadFile(items[items.length - 1]?.file);
        }}
      />
      <Typography variant="body2">
        <strong>Expected columns</strong> - row 1 must contain headers. Column names are matched case-insensitively;
        rows can span multiple warehouses in a single file. A blank Effective Date defaults to today.
      </Typography>
      <Box mt={1}>
        {BULK_IMPORT_COLUMNS.map(column => (
          <Chip
            key={column.key}
            size="small"
            variant="outlined"
            className={classes.columnChip}
            label={column.optional ? `${column.label} (optional)` : column.label}
          />
        ))}
      </Box>
      <Box mt={1.5}>
        <Typography variant="body2">
          <strong>Valid warehouses:</strong> {warehouses.map(warehouse => warehouse.name).join(' · ')}
        </Typography>
      </Box>
      <Box mt={1.5}>
        <Button testId="downloadDebtTrackingTemplate" variant="outlined" size="small" color="primary"
          startIcon={<GetApp />} onClick={downloadBulkImportTemplate}>
          Download template
        </Button>
      </Box>
      {uploadError && <Box mt={2}><Alert severity="error">{uploadError}</Alert></Box>}
    </Box>
  );

  const previewStep = (
    <Box m={2} flexGrow={1} minWidth={0}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">
          <strong>{bulkImportPreview?.fileName}</strong> · {pluralize(rows.length, 'row')} parsed
        </Typography>
        <Link component="button" variant="body2" onClick={() => debtTrackingStore.resetBulkImport()}>
          Choose different file
        </Link>
      </Box>
      {notFoundLoanIds.length > 0 && (
        <Box mt={2}>
          <Alert severity="error">
            Loan IDs not found (or paid off): <strong>{notFoundLoanIds.join(', ')}</strong>.
            Fix them in the file and choose it again.
          </Alert>
        </Box>
      )}
      <Box my={2}>
        <Grid container spacing={2}>
          {summaryCell('Will import', includedBulkImportRows.length)}
          {summaryCell('New loans', countOf(BulkImportAction.NEW))}
          {summaryCell('Updates', countOf(BulkImportAction.UPDATE))}
          {summaryCell('Errors', errorCount, errorCount > 0)}
        </Grid>
      </Box>
      <Box mb={1}>
        <ToggleButtonGroup size="small" exclusive value={previewFilter} onChange={(e, value) => { if (value) { setPreviewFilter(value); setPreviewPage(1); } }}>
          {PREVIEW_FILTERS.map(filter => (
            <ToggleButton key={filter.value} value={filter.value}>
              {filter.label} ({countOf(filter.value)})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <TableContainer className={classes.previewTable}>
        {visibleRows.length === 0 ? (
          <Box p={2}><Typography color="textSecondary">No rows match this filter.</Typography></Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                {['Row', 'Warehouse', 'Loan ID', 'Financing Amount', 'Effective Date', 'Comment', 'Action'].map(header => (
                  <TableCell key={header} align={header === 'Financing Amount' ? 'right' : 'left'}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map(row => {
                const hasErrors = row.action === BulkImportAction.ERROR;
                return (
                  <TableRow key={row.rowNumber} className={hasErrors ? classes.errorRow : ''}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={!!row.include}
                        disabled={hasErrors}
                        onChange={() => debtTrackingStore.toggleBulkImportRow(row.rowNumber)}
                      />
                    </TableCell>
                    <TableCell>{row.rowNumber}</TableCell>
                    <TableCell>{row.warehouseName || '-'}</TableCell>
                    <TableCell>{row.loanId || '-'}</TableCell>
                    <TableCell align="right">{hasErrors && !row.amount ? '-' : formatMoney(row.amount)}</TableCell>
                    <TableCell>{formatDisplayDate(row.effectiveDate) || '-'}</TableCell>
                    <TableCell>
                      {hasErrors
                        ? <span className={classes.issues}>{row.issues.join(' · ')}</span>
                        : row.comment || <em>No comment</em>}
                    </TableCell>
                    <TableCell>{actionBadge(row)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {filteredRows.length > 0 && (
        <PaginationBar
          start={previewStart + 1}
          pageSize={BULK_IMPORT_PREVIEW_PAGE_SIZE}
          pageNumber={previewPageNumber}
          totalPages={previewTotalPages}
          totalRecords={filteredRows.length}
          className={classes.pagination}
          onChange={(e, page) => setPreviewPage(page)}
        />
      )}
      {errorCount > 0 && (
        <Box mt={2}>
          <Alert severity="warning">
            {pluralize(errorCount, 'row')} will be skipped due to errors. Fix them in the source file and re-upload,
            or proceed and the remaining {pluralize(includedBulkImportRows.length, 'row')} will be applied.
          </Alert>
        </Box>
      )}
    </Box>
  );

  const doneStep = bulkImportResult && (
    <Box m={2} flexGrow={1} minWidth={0}>
      <Grid container spacing={2}>
        {summaryCell('Rows applied', bulkImportResult.newLoans + bulkImportResult.loansUpdated)}
        {summaryCell('New loans', bulkImportResult.newLoans)}
        {summaryCell('Loans updated', bulkImportResult.loansUpdated)}
      </Grid>
    </Box>
  );

  const step = bulkImportResult ? 'done' : bulkImportPreview ? 'preview' : 'upload';

  return (
    <StandardDialog
      open={open}
      title={step === 'done' ? 'Import Complete' : 'Bulk Import'}
      subTitle={step === 'preview' && affectedWarehouses
        ? `Will apply ${pluralize(includedBulkImportRows.length, 'row')} across ${pluralize(affectedWarehouses, 'warehouse')}`
        : 'Rows are routed to their warehouse using the Warehouse Line column'}
      handleClose={onClose}
      maxWidth={step === 'upload' ? 'sm' : 'lg'}
      removeContentBox
      dialogContent={step === 'done' ? doneStep : step === 'preview' ? previewStep : uploadStep}
      dialogActions={
        step === 'done' ? (
          <Button testId="closeBulkImport" variant="contained" color="primary" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button testId="cancelBulkImport" variant="outlined" color="primary" onClick={onClose}>Cancel</Button>
            {step === 'preview' && (
              <Button testId="applyBulkImport" variant="contained" color="primary"
                disabled={!includedBulkImportRows.length || isWorking} onClick={applyImport}>
                Import {pluralize(includedBulkImportRows.length, 'Row')}
              </Button>
            )}
          </>
        )
      }
    />
  );
});
